// BuildingDetailsModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./BuildingDetailsModal.css";
import Select from "react-select";
import { applyImageFallback, documentThumbnailUrl } from "../utils/documentPreviews";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    Tooltip,
    useMap
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Recharts for the timeline chart
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
} from "recharts";

// Marker clustering for Leaflet
import MarkerClusterGroup from "react-leaflet-cluster";

// ----- Default single-marker icon -----
const defaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

// ----- Custom cluster icon (with number) -----
function createClusterIcon(cluster) {
    const count = cluster.getChildCount();

    // Size buckets: small / medium / large
    let sizeClass = "small";
    if (count >= 10 && count < 50) sizeClass = "medium";
    else if (count >= 50) sizeClass = "large";

    return L.divIcon({
        html: `<div class="cluster-inner">${count}</div>`,
        className: `custom-cluster ${sizeClass}`,
        iconSize: [40, 40],
    });
}

function BuildingMapViewport({ buildings, selectedBuilding }) {
    const map = useMap();

    useEffect(() => {
        if (selectedBuilding?.latitude && selectedBuilding?.longitude) {
            map.flyTo(
                [selectedBuilding.latitude, selectedBuilding.longitude],
                15,
                { duration: 0.8 }
            );
            return;
        }

        const points = buildings
            .filter((building) => building.latitude && building.longitude)
            .map((building) => [building.latitude, building.longitude]);

        if (points.length === 1) {
            map.setView(points[0], 14);
        } else if (points.length > 1) {
            map.fitBounds(points, { padding: [36, 36], maxZoom: 13 });
        }
    }, [map, buildings, selectedBuilding?.id, selectedBuilding?.latitude, selectedBuilding?.longitude]);

    return null;
}

function BuildingDetailsModal({ onClose, onSave, onDelete, initialBuildingId }) {
    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [errors, setErrors] = useState({});
    const [showMap, setShowMap] = useState(false);
    const [selectedMapBuildingId, setSelectedMapBuildingId] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [buildingContext, setBuildingContext] = useState({
        locationHistory: [],
        documents: [],
    });

    const [imagePaths, setImagePaths] = useState([]);
    const [imageCaptions, setImageCaptions] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const [newImageFiles, setNewImageFiles] = useState([]);
    const [newImageCaptions, setNewImageCaptions] = useState([]);

    // Timeline & filter state
    const [minYear, setMinYear] = useState(1900);
    const [maxYear, setMaxYear] = useState(2024);
    const [filterStartYear, setFilterStartYear] = useState(null);
    const [filterEndYear, setFilterEndYear] = useState(null);

    // UX toggles
    const [useCluster, setUseCluster] = useState(true);
    const [useDecadeBuckets, setUseDecadeBuckets] = useState(false);

    const overviewFields = [
        "name",
        "status",
        "building_type",
        "primary_usage",
        "historical_status",
        "description",
        "notes",
        "established_date",
        "decommission_date",
    ];
    const addressFields = [
        "street",
        "city",
        "county",
        "postcode",
        "country",
        "latitude",
        "longitude",
        "primary_contact",
        "phone_number",
    ];
    const detailsFields = [
        "capacity",
        "land_area",
        "ownership_status",
        "maintenance_contact",
        "builder",
        "consecrated_by",
        "architect",
        "architect_name",
    ];

    const visibleFieldsByTab = {
        overview: overviewFields,
        address: addressFields,
        details: detailsFields,
    };

    /* ---------- Load buildings on mount ---------- */
    useEffect(() => {
        fetchBuildings();
    }, []);

    useEffect(() => {
        if (!initialBuildingId || !buildings.length || selectedBuilding?.id === initialBuildingId) return;
        handleBuildingSelect(initialBuildingId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildings, initialBuildingId, selectedBuilding?.id]);

    useEffect(() => {
        if (showMap && selectedBuilding?.id) {
            setSelectedMapBuildingId(selectedBuilding.id);
        }
    }, [showMap, selectedBuilding?.id]);

    const fetchBuildings = async () => {
        try {
            const res = await axios.get("/api/buildings");
            const rows = res.data || [];
            setBuildings(rows);
        } catch (error) {
            console.error("Error fetching buildings:", error);
        }
    };

    /* ---------- Derive minYear / maxYear from established_date ---------- */
    useEffect(() => {
        if (!buildings.length) return;

        const years = buildings
            .map((b) => {
                if (!b.established_date) return null;
                const d = new Date(b.established_date);
                const y = d.getFullYear();
                return Number.isFinite(y) ? y : null;
            })
            .filter((y) => y !== null);

        if (!years.length) {
            setMinYear(1900);
            setMaxYear(2024);
            if (filterStartYear === null) setFilterStartYear(1900);
            if (filterEndYear === null) setFilterEndYear(2024);
            return;
        }

        const dynamicMinYear = Math.min(...years);
        const dynamicMaxYear = Math.max(...years);

        setMinYear(dynamicMinYear);
        setMaxYear(dynamicMaxYear);

        if (filterStartYear === null) {
            setFilterStartYear(dynamicMinYear);
        }
        if (filterEndYear === null) {
            setFilterEndYear(dynamicMaxYear);
        }
    }, [buildings]);

    /* ---------- Date helpers ---------- */

    const convertToSAST = (utcDate) => {
        if (!utcDate) return "";
        const date = new Date(utcDate);
        const offsetInMs = 2 * 60 * 60 * 1000; // UTC+2
        const sastDate = new Date(date.getTime() + offsetInMs);
        return sastDate.toISOString().split("T")[0];
    };

    const formatToSAST = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const offsetInMs = 2 * 60 * 60 * 1000; // UTC+2
        const sastDate = new Date(date.getTime() + offsetInMs);
        return sastDate.toISOString().split("T")[0];
    };

    const getYear = (dateString) => {
        if (!dateString) return null;
        const year = new Date(dateString).getFullYear();
        return Number.isFinite(year) ? year : null;
    };

    const getBuildingAddress = (building = {}) => {
        return [
            building.street,
            building.city,
            building.county,
            building.postcode,
            building.country,
        ].filter(Boolean).join(", ");
    };

    const getBuildingPeriodLabel = (building = {}) => {
        const established = building.established_date
            ? formatToSAST(building.established_date)
            : "Unknown";
        const decommissioned = building.decommission_date
            ? formatToSAST(building.decommission_date)
            : "Present/unknown";
        return `${established} - ${decommissioned}`;
    };

    const getBuildingStatusLabel = (building = {}) => {
        if (building.decommission_date) return "Decommissioned";
        if (building.established_date) return "Active";
        return building.status || "Unplaced";
    };

    /* ---------- Form helpers ---------- */

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
        }
    };

    const validateForm = () => {
        const requiredFields = [
            "name",
            "street",
            "city",
            "country",
            "latitude",
            "longitude",
        ];
        const newErrors = {};

        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field].toString().trim() === "") {
                newErrors[field] = `${field.replace(/_/g, " ")} is required.`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---------- Selecting a building ---------- */

    const handleBuildingSelect = async (id) => {
        if (!id) {
            setSelectedBuilding(null);
            setFormData({});
            setIsAddingNew(false);
            setImagePaths([]);
            setImageCaptions([]);
            setCurrentImageIndex(0);
            setNewImageFiles([]);
            setNewImageCaptions([]);
            setBuildingContext({ locationHistory: [], documents: [] });
            setActiveTab("overview");
            return;
        }

        if (id === "new") {
            setSelectedBuilding(null);
            setFormData({});
            setIsAddingNew(true);
            setImagePaths([]);
            setImageCaptions([]);
            setCurrentImageIndex(0);
            setNewImageFiles([]);
            setNewImageCaptions([]);
            setBuildingContext({ locationHistory: [], documents: [] });
            setActiveTab("overview");
            return;
        }

        setLoading(true);
        setIsAddingNew(false);

        try {
            const res = await axios.get(`/api/buildings/${id}`);
            const building = res.data;

            const transformedBuilding = {
                ...building,
                established_date: building.established_date
                    ? convertToSAST(building.established_date)
                    : "",
                decommission_date: building.decommission_date
                    ? convertToSAST(building.decommission_date)
                    : "",
            };

            setSelectedBuilding(transformedBuilding);
            setFormData(transformedBuilding);
            setImagePaths(building.image_paths || []);
            setImageCaptions(building.image_captions || []);
            setCurrentImageIndex(0);
            setNewImageFiles([]);
            setNewImageCaptions([]);
            setActiveTab("overview");

            try {
                const contextRes = await axios.get(`/api/buildings/${id}/context`);
                setBuildingContext({
                    locationHistory: contextRes.data.locationHistory || [],
                    documents: contextRes.data.documents || [],
                });
            } catch (contextError) {
                console.error("Error fetching building context:", contextError);
                setBuildingContext({ locationHistory: [], documents: [] });
            }
        } catch (error) {
            console.error("Error fetching building details:", error);
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Save / delete ---------- */

    const handleSave = async () => {
        if (!validateForm()) {
            alert("Please fill in all required fields.");
            return;
        }

        try {
            const processedData = { ...formData };
            if (processedData.established_date === "")
                processedData.established_date = null;
            if (processedData.decommission_date === "")
                processedData.decommission_date = null;

            let buildingId;
            const payload = {
                ...processedData,
                image_paths: imagePaths,
                image_captions: imageCaptions,
            };

            let res;
            if (selectedBuilding) {
                res = await axios.put(
                    `/api/buildings/${selectedBuilding.id}`,
                    payload
                );
            } else {
                res = await axios.post("/api/buildings", payload);
            }
            buildingId = res.data.id;

            for (let i = 0; i < newImageFiles.length; i++) {
                const file = newImageFiles[i];
                const caption = newImageCaptions[i] || "";

                const uploadData = new FormData();
                uploadData.append("building_id", buildingId);
                uploadData.append("image", file);
                uploadData.append("caption", caption);

                const uploadRes = await axios.post(
                    "/api/upload-image-building",
                    uploadData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );

                setImagePaths(uploadRes.data.imagePaths);
                setImageCaptions(uploadRes.data.imageCaptions);
            }

            setNewImageFiles([]);
            setNewImageCaptions([]);
            setCurrentImageIndex(
                (prev) => (imagePaths.length ? imagePaths.length - 1 : prev)
            );

            alert("Building saved successfully!");
            await fetchBuildings();
            if (typeof onSave === "function") onSave();
        } catch (error) {
            console.error("Error saving building:", error);
            alert("Failed to save building.");
        }
    };

    const handleDelete = async () => {
        if (!selectedBuilding) return;
        if (window.confirm("Are you sure you want to delete this building?")) {
            try {
                await axios.delete(`/api/buildings/${selectedBuilding.id}`);
                await fetchBuildings();
                setSelectedBuilding(null);
                setFormData({});
                alert("Building deleted successfully!");
                if (typeof onDelete === "function") onDelete();
            } catch (error) {
                console.error("Error deleting building:", error);
                alert("Error deleting building. Please try again.");
            }
        }
    };

    /* ---------- Map helpers ---------- */

    function getMarkerColor(building) {
        if (building.decommission_date) return "#dc2626";
        if (building.established_date) return "#16a34a";
        return "#4b5563";
    }

    function createCircleIcon(color, isSelected = false) {
        const size = isSelected ? 30 : 24;
        const innerSize = isSelected ? 14 : 10;
        const ringColor = isSelected ? "#f59e0b" : "#ffffff";

        return L.divIcon({
            html: `<div style="
          width: ${size}px;
          height: ${size}px;
          display: grid;
          place-items: center;
          background-color: ${color};
          border-radius: 50%;
          border: 4px solid ${ringColor};
          box-shadow: 0 0 0 2px rgba(15,23,42,0.85), 0 6px 14px rgba(15,23,42,0.45);
        "><span style="
          width: ${innerSize}px;
          height: ${innerSize}px;
          display: block;
          border-radius: 50%;
          background: #ffffff;
          opacity: 0.95;
        "></span></div>`,
            className: "",
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
        });
    }

    const filteredBuildings = useMemo(() => {
        if (!buildings.length) return [];
        const start = filterStartYear ?? minYear;
        const end = filterEndYear ?? maxYear;

        return buildings.filter((b) => {
            if (!b.established_date) return false;
            const y = new Date(b.established_date).getFullYear();
            if (!Number.isFinite(y)) return false;
            return y >= start && y <= end;
        });
    }, [buildings, filterStartYear, filterEndYear, minYear, maxYear]);

    const mappedBuildings = useMemo(() => {
        return filteredBuildings
            .filter((building) => building.latitude && building.longitude)
            .sort((a, b) => {
                const aYear = getYear(a.established_date) ?? 9999;
                const bYear = getYear(b.established_date) ?? 9999;
                if (aYear !== bYear) return aYear - bYear;
                return (a.name || "").localeCompare(b.name || "");
            });
    }, [filteredBuildings]);

    const selectedMapBuilding = useMemo(() => {
        if (!selectedMapBuildingId) return null;
        return mappedBuildings.find((building) => building.id === selectedMapBuildingId) || null;
    }, [mappedBuildings, selectedMapBuildingId]);

    const mapSummary = useMemo(() => {
        let activeInRange = 0;
        let decommissionedInRange = 0;
        let withImages = 0;

        filteredBuildings.forEach((building) => {
            if (building.decommission_date) {
                decommissionedInRange++;
            } else {
                activeInRange++;
            }
            if (building.image_paths && building.image_paths.length) {
                withImages++;
            }
        });

        return {
            mapped: mappedBuildings.length,
            activeInRange,
            decommissionedInRange,
            withImages,
        };
    }, [filteredBuildings, mappedBuildings]);

    /* ---------- Timeline data: opening + closing EVENTS per year ---------- */

    const timelineData = useMemo(() => {
        if (!buildings.length) {
            return { data: [], minYear: null, maxYear: null };
        }

        const openCounts = {};
        const closeCounts = {};

        buildings.forEach((b) => {
            if (b.established_date) {
                const d = new Date(b.established_date);
                const y = d.getFullYear();
                if (Number.isFinite(y)) {
                    openCounts[y] = (openCounts[y] || 0) + 1;
                }
            }
            if (b.decommission_date) {
                const d2 = new Date(b.decommission_date);
                const y2 = d2.getFullYear();
                if (Number.isFinite(y2)) {
                    closeCounts[y2] = (closeCounts[y2] || 0) + 1;
                }
            }
        });

        const years = new Set([
            ...Object.keys(openCounts).map((y) => parseInt(y, 10)),
            ...Object.keys(closeCounts).map((y) => parseInt(y, 10)),
        ]);

        if (!years.size) {
            return { data: [], minYear: null, maxYear: null };
        }

        const localMin = Math.min(...years);
        const localMax = Math.max(...years);

        const data = [];
        for (let y = localMin; y <= localMax; y++) {
            const openings = openCounts[y] || 0;
            const closures = closeCounts[y] || 0;
            data.push({ year: y, openings, closures });
        }

        return { data, minYear: localMin, maxYear: localMax };
    }, [buildings]);

    /* ---------- Filter timeline by slider range (for chart) ---------- */

    const filteredTimelineDataForChart = useMemo(() => {
        if (!timelineData.data.length) return [];
        const start = filterStartYear ?? timelineData.minYear ?? minYear;
        const end = filterEndYear ?? timelineData.maxYear ?? maxYear;

        return timelineData.data.filter(
            (d) => d.year >= start && d.year <= end
        );
    }, [timelineData, filterStartYear, filterEndYear, minYear, maxYear]);

    /* ---------- Chart data: per year OR per decade ---------- */

    const chartData = useMemo(() => {
        if (!filteredTimelineDataForChart.length) return [];

        if (!useDecadeBuckets) {
            // yearly, already filtered by range
            return filteredTimelineDataForChart;
        }

        // group into decades, but only for the filtered years
        const decadeMap = {};
        filteredTimelineDataForChart.forEach(({ year, openings, closures }) => {
            const decadeStart = Math.floor(year / 10) * 10;
            if (!decadeMap[decadeStart]) {
                decadeMap[decadeStart] = {
                    year: decadeStart,
                    label: `${decadeStart}s`,
                    openings: 0,
                    closures: 0,
                };
            }
            decadeMap[decadeStart].openings += openings;
            decadeMap[decadeStart].closures += closures;
        });

        return Object.values(decadeMap).sort((a, b) => a.year - b.year);
    }, [filteredTimelineDataForChart, useDecadeBuckets]);

    const chartMeta = useMemo(() => {
        if (!chartData.length) {
            return { bucketCount: 0, maxBucketTotal: 0 };
        }
        let maxBucketTotal = 0;
        chartData.forEach((d) => {
            const total = (d.openings || 0) + (d.closures || 0);
            if (total > maxBucketTotal) maxBucketTotal = total;
        });
        return { bucketCount: chartData.length, maxBucketTotal };
    }, [chartData]);

    const chartGranularityLabel = useDecadeBuckets ? "per decade" : "per year";

    const currentRangeLabel = useMemo(() => {
        const start = filterStartYear ?? timelineData.minYear ?? minYear;
        const end = filterEndYear ?? timelineData.maxYear ?? maxYear;
        return `${start}–${end}`;
    }, [filterStartYear, filterEndYear, timelineData, minYear, maxYear]);

    const selectedImagePath = imagePaths[currentImageIndex] || "";
    const selectedImageCaption = imageCaptions[currentImageIndex] || "No caption";
    const hasMultipleImages = imagePaths.length > 1;
    const hasCoordinates = Boolean(formData.latitude && formData.longitude);
    const fullAddress = [
        formData.street,
        formData.city,
        formData.county,
        formData.postcode,
        formData.country,
    ].filter(Boolean).join(", ");

    const openMapLink = () => {
        if (hasCoordinates) {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`,
                "_blank",
                "noopener,noreferrer"
            );
            return;
        }
        if (fullAddress) {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    const isImageDocument = (doc) => {
        const value = `${doc.file_path || ""} ${doc.filename || ""} ${doc.file_type_description || ""}`;
        return /\.(png|jpe?g|gif|webp|bmp|tiff?)\b/i.test(value) || /image/i.test(value);
    };

    const openDocument = (doc) => {
        if (doc.file_path) {
            window.open(doc.file_path, "_blank", "noopener,noreferrer");
        }
    };

    const renderField = (key) => (
        <div className="building-form-group" key={key}>
            <label>{key.replace(/_/g, " ")}</label>
            {key === "historical_status" ? (
                <label className="building-checkbox-row">
                    <input
                        type="checkbox"
                        name={key}
                        checked={formData[key] || false}
                        onChange={handleInputChange}
                        className={errors[key] ? "error" : ""}
                    />
                    Historically significant
                </label>
            ) : key === "description" || key === "notes" ? (
                <textarea
                    name={key}
                    value={formData[key] || ""}
                    onChange={handleInputChange}
                    className={errors[key] ? "error" : ""}
                    rows="5"
                />
            ) : key === "established_date" || key === "decommission_date" ? (
                <input
                    type="date"
                    name={key}
                    value={formData[key] || ""}
                    onChange={handleInputChange}
                    className={errors[key] ? "error" : ""}
                />
            ) : (
                <input
                    type={
                        key === "land_area" ||
                        key === "capacity" ||
                        key === "latitude" ||
                        key === "longitude"
                            ? "number"
                            : "text"
                    }
                    step={
                        key === "latitude" || key === "longitude"
                            ? "0.000001"
                            : undefined
                    }
                    name={key}
                    value={formData[key] || ""}
                    onChange={handleInputChange}
                    className={errors[key] ? "error" : ""}
                />
            )}
            {errors[key] && (
                <p className="error-message">{errors[key]}</p>
            )}
        </div>
    );

    /* ---------- Render ---------- */

    return (
        <div className="building-modal-overlay">
            <div className="building-modal-content">
                <button className="building-close-btn" onClick={onClose}>
                    &times;
                </button>
                <div className="building-modal-header">
                    <div>
                        <p className="building-eyebrow">Temples & meeting places</p>
                        <h2>{isAddingNew ? "Add New Building" : "Temple Workspace"}</h2>
                    </div>
                    <button
                        className="building-map-btn"
                        onClick={() => setShowMap(true)}
                    >
                        Show Map & Timeline
                    </button>
                </div>

                {/* Select building */}
                <div className="building-form-group">
                    <label>Select Building</label>
                    <Select
                        options={[
                            { value: "new", label: "Add New Building" },
                            ...buildings.map((building) => ({
                                value: building.id,
                                label: building.name,
                            })),
                        ]}
                        onChange={(selectedOption) =>
                            handleBuildingSelect(
                                selectedOption ? selectedOption.value : null
                            )
                        }
                        value={
                            isAddingNew
                                ? { value: "new", label: "Add New Building" }
                                : selectedBuilding
                                    ? {
                                        value: selectedBuilding.id,
                                        label: selectedBuilding.name,
                                    }
                                    : null
                        }
                        isSearchable
                        isClearable
                        placeholder="Choose or Add New Building"
                    />
                </div>

                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <div className="building-workspace">
                        <aside className="building-profile-panel">
                            <div className="building-image-hero">
                                {selectedImagePath ? (
                                    <>
                                        <img
                                            src={documentThumbnailUrl(selectedImagePath)}
                                            alt=""
                                            onError={(event) => applyImageFallback(event, selectedImagePath)}
                                        />
                                        {hasMultipleImages && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="building-image-nav previous"
                                                    aria-label="Previous temple image"
                                                    onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? imagePaths.length - 1 : prev - 1))}
                                                >
                                                    ‹
                                                </button>
                                                <button
                                                    type="button"
                                                    className="building-image-nav next"
                                                    aria-label="Next temple image"
                                                    onClick={() => setCurrentImageIndex((prev) => (prev + 1) % imagePaths.length)}
                                                >
                                                    ›
                                                </button>
                                                <span className="building-image-count">
                                                    {currentImageIndex + 1} / {imagePaths.length}
                                                </span>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className="building-image-empty">
                                        No temple image yet
                                    </div>
                                )}
                            </div>
                            {hasMultipleImages && (
                                <div className="building-image-strip" aria-label="Temple image thumbnails">
                                    {imagePaths.map((path, index) => (
                                        <button
                                            type="button"
                                            key={`${path}-profile-${index}`}
                                            className={currentImageIndex === index ? "active" : ""}
                                            onClick={() => setCurrentImageIndex(index)}
                                            aria-label={`Show temple image ${index + 1}`}
                                        >
                                            <img
                                                src={documentThumbnailUrl(path)}
                                                alt=""
                                                onError={(event) => applyImageFallback(event, path)}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedImagePath && (
                                <p className="building-image-caption">{selectedImageCaption}</p>
                            )}
                            <h3>{formData.name || "Select or add a temple"}</h3>
                            <div className="building-profile-chips">
                                {formData.status && <span>{formData.status}</span>}
                                {formData.building_type && <span>{formData.building_type}</span>}
                                {formData.historical_status && <span>Historic</span>}
                                {hasCoordinates ? <span>Mapped</span> : <span>Needs coordinates</span>}
                            </div>
                            <dl className="building-profile-list">
                                <dt>Address</dt>
                                <dd>{fullAddress || "No address captured"}</dd>
                                <dt>Active period</dt>
                                <dd>
                                    {formData.established_date || "Unknown"} – {formData.decommission_date || "Present/unknown"}
                                </dd>
                                <dt>Linked lodges</dt>
                                <dd>{buildingContext.locationHistory.length}</dd>
                                <dt>Linked documents</dt>
                                <dd>{buildingContext.documents.length}</dd>
                            </dl>
                            <div className="building-profile-actions">
                                <button type="button" onClick={() => setShowMap(true)}>
                                    Map & Timeline
                                </button>
                                {(hasCoordinates || fullAddress) && (
                                    <button type="button" onClick={openMapLink}>
                                        Open in Google Maps
                                    </button>
                                )}
                            </div>
                            {hasCoordinates && (
                                <div className="building-mini-map">
                                    <MapContainer
                                        key={`${formData.latitude}-${formData.longitude}`}
                                        center={[Number(formData.latitude), Number(formData.longitude)]}
                                        zoom={14}
                                        scrollWheelZoom={false}
                                        zoomControl={false}
                                        style={{ height: "100%", width: "100%" }}
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; OpenStreetMap contributors'
                                        />
                                        <Marker
                                            position={[Number(formData.latitude), Number(formData.longitude)]}
                                            icon={createCircleIcon(getMarkerColor(formData))}
                                        >
                                            <Popup>{formData.name}</Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>
                            )}
                        </aside>

                        <section className="building-editor-panel">
                            <nav className="building-tabs" aria-label="Temple edit tabs">
                                {[
                                    ["overview", "Overview"],
                                    ["address", "Address & Map"],
                                    ["details", "Details"],
                                    ["history", "Lodge Use"],
                                    ["media", "Images"],
                                    ["documents", "Documents"],
                                ].map(([tab, label]) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={activeTab === tab ? "active" : ""}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </nav>

                            {["overview", "address", "details"].includes(activeTab) && (
                                <div className="building-form-grid">
                                    {visibleFieldsByTab[activeTab].map(renderField)}
                                </div>
                            )}

                            {activeTab === "history" && (
                                <div className="building-context-panel">
                                    <h3>Lodges using this temple</h3>
                                    {buildingContext.locationHistory.length === 0 ? (
                                        <p className="building-empty-note">
                                            No lodge location history is linked to this building yet.
                                        </p>
                                    ) : (
                                        <div className="building-timeline-list">
                                            {buildingContext.locationHistory.map((item) => (
                                                <article key={item.id}>
                                                    <strong>{item.lodge_name || item.lodge_code || "Unknown lodge"}</strong>
                                                    <span>
                                                        {formatToSAST(item.start_date)} – {item.end_date ? formatToSAST(item.end_date) : "Present/unknown"}
                                                    </span>
                                                    {item.comments && <p>{item.comments}</p>}
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "media" && (
                                <div className="building-context-panel">
                                    <h3>Temple images</h3>
                                    {imagePaths.length > 0 ? (
                                        <div className="building-gallery">
                                            {imagePaths.map((path, index) => (
                                                <button
                                                    type="button"
                                                    key={`${path}-${index}`}
                                                    className={currentImageIndex === index ? "active" : ""}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                >
                                                    <img
                                                        src={documentThumbnailUrl(path)}
                                                        alt=""
                                                        onError={(event) => applyImageFallback(event, path)}
                                                    />
                                                    <span>{imageCaptions[index] || "No caption"}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="building-empty-note">No uploaded temple images yet.</p>
                                    )}

                                    <div className="building-form-group">
                                        <label>Upload New Images</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files);
                                                setNewImageFiles(files);
                                                setNewImageCaptions(new Array(files.length).fill(""));
                                            }}
                                        />
                                    </div>

                                    <div className="building-form-group">
                                        <label>Captions for New Images (one per line)</label>
                                        <textarea
                                            placeholder="Enter captions, one per line, matching the order of selected images"
                                            value={newImageCaptions.join("\n")}
                                            onChange={(e) => {
                                                const lines = e.target.value.split("\n");
                                                const updatedCaptions = lines.slice(0, newImageFiles.length);
                                                while (updatedCaptions.length < newImageFiles.length) {
                                                    updatedCaptions.push("");
                                                }
                                                setNewImageCaptions(updatedCaptions);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === "documents" && (
                                <div className="building-context-panel">
                                    <h3>Linked documents</h3>
                                    <div className="building-form-group">
                                        <label>Document IDs (comma-separated)</label>
                                        <input
                                            type="text"
                                            name="document_ids"
                                            value={(Array.isArray(formData.document_ids) ? formData.document_ids : []).join(", ")}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    document_ids: e.target.value
                                                        .split(",")
                                                        .map((id) => id.trim())
                                                        .filter(Boolean),
                                                })
                                            }
                                        />
                                    </div>

                                    {buildingContext.documents.length === 0 ? (
                                        <p className="building-empty-note">
                                            No linked document records found for the captured IDs.
                                        </p>
                                    ) : (
                                        <div className="building-doc-list">
                                            {buildingContext.documents.map((doc) => (
                                                <article key={doc.id}>
                                                    {isImageDocument(doc) && doc.file_path && (
                                                        <img
                                                            src={documentThumbnailUrl(doc.file_path)}
                                                            alt=""
                                                            onError={(event) => applyImageFallback(event, doc.file_path)}
                                                        />
                                                    )}
                                                    <div>
                                                        <strong>{doc.title || doc.filename || doc.id}</strong>
                                                        <span>
                                                            {doc.document_type_description || "Document"} · {doc.file_type_description || "File"}
                                                        </span>
                                                        {doc.description && <p>{doc.description}</p>}
                                                        <button type="button" onClick={() => openDocument(doc)}>
                                                            Open Document
                                                        </button>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                )}

                <div className="building-modal-actions">
                    {selectedBuilding && (
                        <button
                            className="building-delete-btn"
                            onClick={handleDelete}
                        >
                            Delete
                        </button>
                    )}
                    <button className="building-save-btn" onClick={handleSave}>
                        Save
                    </button>
                    <button className="building-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                </div>

                {/* MAP + TIMELINE POPUP MODAL */}
                {showMap && (
                    <div className="map-modal-overlay">
                        <div className="map-modal-content">
                            <button
                                className="map-close-btn"
                                onClick={() => setShowMap(false)}
                            >
                                &times;
                            </button>
                            <div className="map-dashboard">
                                <header className="map-dashboard-header">
                                    <div>
                                        <p className="building-eyebrow">Mapped archive</p>
                                        <h2>Building Map & Timeline</h2>
                                        <p>
                                            Trace temple locations, establishment dates, decommission dates, and image evidence together.
                                        </p>
                                    </div>
                                    <div className="map-summary-grid" aria-label="Map summary">
                                        <div>
                                            <strong>{mapSummary.mapped}</strong>
                                            <span>mapped places</span>
                                        </div>
                                        <div>
                                            <strong>{mapSummary.activeInRange}</strong>
                                            <span>active in range</span>
                                        </div>
                                        <div>
                                            <strong>{mapSummary.decommissionedInRange}</strong>
                                            <span>closed in range</span>
                                        </div>
                                        <div>
                                            <strong>{mapSummary.withImages}</strong>
                                            <span>with images</span>
                                        </div>
                                    </div>
                                </header>

                                <section className="map-controls-panel" aria-label="Map controls">
                                    <div className="map-toggle-row">
                                        <label className="map-toggle">
                                            <input
                                                type="checkbox"
                                                checked={useCluster}
                                                onChange={(e) => setUseCluster(e.target.checked)}
                                            />
                                            <span>Cluster markers</span>
                                        </label>
                                        <label className="map-toggle">
                                            <input
                                                type="checkbox"
                                                checked={useDecadeBuckets}
                                                onChange={(e) => setUseDecadeBuckets(e.target.checked)}
                                            />
                                            <span>Group by decade</span>
                                        </label>
                                    </div>
                                    <div className="map-range-controls">
                                        <label>
                                            <span>From <strong>{filterStartYear ?? minYear}</strong></span>
                                            <input
                                                type="range"
                                                min={timelineData.minYear || minYear}
                                                max={timelineData.maxYear || maxYear}
                                                value={filterStartYear ?? timelineData.minYear ?? minYear}
                                                onChange={(e) => {
                                                    const v = Number(e.target.value);
                                                    setFilterStartYear(v);
                                                    if (filterEndYear !== null && v > filterEndYear) {
                                                        setFilterEndYear(v);
                                                    }
                                                }}
                                            />
                                        </label>
                                        <label>
                                            <span>To <strong>{filterEndYear ?? maxYear}</strong></span>
                                            <input
                                                type="range"
                                                min={timelineData.minYear || minYear}
                                                max={timelineData.maxYear || maxYear}
                                                value={filterEndYear ?? timelineData.maxYear ?? maxYear}
                                                onChange={(e) => {
                                                    const v = Number(e.target.value);
                                                    setFilterEndYear(v);
                                                    if (filterStartYear !== null && v < filterStartYear) {
                                                        setFilterStartYear(v);
                                                    }
                                                }}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFilterStartYear(timelineData.minYear ?? minYear);
                                                setFilterEndYear(timelineData.maxYear ?? maxYear);
                                            }}
                                        >
                                            Reset range
                                        </button>
                                    </div>
                                </section>

                                <section className="map-timeline-panel">
                                    <div className="map-panel-heading">
                                        <div>
                                            <h3>Opening & Closing Events {chartGranularityLabel}</h3>
                                            <p>One building can appear twice when it has both an opening and a closing date.</p>
                                        </div>
                                        <div>
                                            <span>Buckets: {chartMeta.bucketCount}</span>
                                            <span>Max: {chartMeta.maxBucketTotal}</span>
                                            <span>{currentRangeLabel}</span>
                                        </div>
                                    </div>

                                    {chartData.length === 0 ? (
                                        <p className="map-empty-state">
                                            No dated records in this range.
                                        </p>
                                    ) : (
                                        <div className="map-chart-frame">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={chartData}
                                                    margin={{ top: 10, right: 10, bottom: 20, left: 30 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis
                                                        dataKey={useDecadeBuckets ? "label" : "year"}
                                                        tick={{ fontSize: 10 }}
                                                        tickMargin={6}
                                                        interval={useDecadeBuckets ? 0 : 9}
                                                    />
                                                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                                    <RechartsTooltip
                                                        cursor={{ fill: "rgba(148,163,184,0.2)" }}
                                                        formatter={(value, name, props) => {
                                                            const isOpened = props?.dataKey === "openings";
                                                            return [
                                                                `${value} event${value === 1 ? "" : "s"}`,
                                                                isOpened ? "Opened" : "Decommissioned",
                                                            ];
                                                        }}
                                                        labelFormatter={(label, payload) => {
                                                            if (useDecadeBuckets) {
                                                                return payload?.[0]?.payload?.label || label;
                                                            }
                                                            return `Year: ${label}`;
                                                        }}
                                                    />
                                                    <Legend verticalAlign="top" height={24} iconSize={8} />
                                                    <Bar dataKey="openings" name="Opened" stackId="timeline" fill="#22c55e" />
                                                    <Bar dataKey="closures" name="Decommissioned" stackId="timeline" fill="#ef4444" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </section>

                                <section className="map-workspace-grid">
                                    <div className="map-canvas-panel">
                                        <MapContainer
                                            center={[-25.7461, 28.1881]}
                                            zoom={5}
                                            className="map-canvas"
                                            scrollWheelZoom={true}
                                            zoomAnimation={true}
                                            zoomControl={true}
                                        >
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            />
                                            <BuildingMapViewport
                                                buildings={mappedBuildings}
                                                selectedBuilding={selectedMapBuilding}
                                            />

                                            {useCluster ? (
                                                <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon}>
                                                    {mappedBuildings.map((building) => {
                                                        const color = getMarkerColor(building);
                                                        const icon = createCircleIcon(
                                                            color,
                                                            building.id === selectedMapBuildingId
                                                        );

                                                        return (
                                                            <Marker
                                                                key={building.id}
                                                                position={[building.latitude, building.longitude]}
                                                                icon={icon}
                                                                eventHandlers={{
                                                                    click: () => setSelectedMapBuildingId(building.id),
                                                                }}
                                                            >
                                                                <Tooltip>{building.name}</Tooltip>
                                                                <Popup>
                                                                    <div className="map-popup">
                                                                        <strong>{building.name}</strong>
                                                                        <span>{getBuildingAddress(building) || "Address not recorded"}</span>
                                                                        <span>{getBuildingPeriodLabel(building)}</span>
                                                                    </div>
                                                                </Popup>
                                                            </Marker>
                                                        );
                                                    })}
                                                </MarkerClusterGroup>
                                            ) : (
                                                mappedBuildings.map((building) => {
                                                    const color = getMarkerColor(building);
                                                    const icon = createCircleIcon(
                                                        color,
                                                        building.id === selectedMapBuildingId
                                                    );

                                                    return (
                                                        <Marker
                                                            key={building.id}
                                                            position={[building.latitude, building.longitude]}
                                                            icon={icon}
                                                            eventHandlers={{
                                                                click: () => setSelectedMapBuildingId(building.id),
                                                            }}
                                                        >
                                                            <Tooltip>{building.name}</Tooltip>
                                                            <Popup>
                                                                <div className="map-popup">
                                                                    <strong>{building.name}</strong>
                                                                    <span>{getBuildingAddress(building) || "Address not recorded"}</span>
                                                                    <span>{getBuildingPeriodLabel(building)}</span>
                                                                </div>
                                                            </Popup>
                                                        </Marker>
                                                    );
                                                })
                                            )}
                                        </MapContainer>
                                    </div>

                                    <aside className="map-research-panel">
                                        <div className="map-panel-heading compact">
                                            <div>
                                                <h3>Places in Range</h3>
                                                <p>{mappedBuildings.length} mapped records from {currentRangeLabel}</p>
                                            </div>
                                        </div>
                                        {selectedMapBuilding ? (
                                            <article className="map-selected-building">
                                                {selectedMapBuilding.image_paths?.[0] && (
                                                    <img
                                                        src={documentThumbnailUrl(selectedMapBuilding.image_paths[0])}
                                                        alt=""
                                                        onError={(event) => applyImageFallback(event, selectedMapBuilding.image_paths[0])}
                                                    />
                                                )}
                                                <div>
                                                    <span className="chip chip-neutral">{getBuildingStatusLabel(selectedMapBuilding)}</span>
                                                    <h3>{selectedMapBuilding.name}</h3>
                                                    <dl>
                                                        <div>
                                                            <dt>Address</dt>
                                                            <dd>{getBuildingAddress(selectedMapBuilding) || "Not recorded"}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Active period</dt>
                                                            <dd>{getBuildingPeriodLabel(selectedMapBuilding)}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Coordinates</dt>
                                                            <dd>{selectedMapBuilding.latitude}, {selectedMapBuilding.longitude}</dd>
                                                        </div>
                                                    </dl>
                                                    {(selectedMapBuilding.notes || selectedMapBuilding.description) && (
                                                        <p>{selectedMapBuilding.notes || selectedMapBuilding.description}</p>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleBuildingSelect(selectedMapBuilding.id);
                                                            setShowMap(false);
                                                        }}
                                                    >
                                                        Open in editor
                                                    </button>
                                                </div>
                                            </article>
                                        ) : (
                                            <div className="map-selected-building map-selected-empty">
                                                <h3>Select a place</h3>
                                                <p>Click a marker or record to focus the map and open the research detail.</p>
                                            </div>
                                        )}
                                        <div className="map-place-list">
                                            {mappedBuildings.map((building) => (
                                                <button
                                                    type="button"
                                                    key={building.id}
                                                    className={building.id === selectedMapBuildingId ? "active" : ""}
                                                    onClick={() => setSelectedMapBuildingId(building.id)}
                                                >
                                                    <strong>{building.name}</strong>
                                                    <span>{building.city || building.street || "Location not recorded"}</span>
                                                    <small>{getBuildingPeriodLabel(building)}</small>
                                                </button>
                                            ))}
                                            {!mappedBuildings.length && (
                                                <p className="map-empty-state">No mapped buildings in this range.</p>
                                            )}
                                        </div>
                                    </aside>
                                </section>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BuildingDetailsModal;
